import { Dtos, formatToDollar } from "@inrev/common";
import { PartialIndicator, Text } from "@portal/components/primitives";
import { Separator } from "@portal/components/ui/Separator";
import { YesNoDisplay } from "@portal/components/ui/YesNoDisplay";
import { FC, type ReactNode } from "react";
import { formatEmpty } from "../../../../../utils/string";

type AccountHistoryCardProps = {
	history: Dtos.SuretyAccount.Metadata.Get.Response;
	emptyPartialFields: Set<string>;
	partialActive: boolean;
	showContractHistory: boolean;
};

const HistoryCardItem = ({
	testId,
	label,
	partial,
	partialActive,
	children,
}: {
	testId: string;
	label: string;
	partial: boolean;
	partialActive: boolean;
	children: ReactNode;
}) => (
	<div data-testid={testId} className="flex items-center space-x-[12px] w-full">
		<Text type="body" color="secondary" className="flex-1">
			{label}
		</Text>
		<div className="flex items-center gap-x-[6px] min-w-fit">
			{children}
			{partial && <PartialIndicator active={partialActive} testId={`${testId}-partial`} />}
		</div>
	</div>
);

export const AccountHistoryCard: FC<AccountHistoryCardProps> = ({
	history,
	emptyPartialFields,
	partialActive,
	showContractHistory,
}) => (
	<div className="flex flex-col space-y-5">
		{showContractHistory && (
			<>
				<HistoryCardItem
					testId="history-largest-project-completed"
					label="Value of largest project completed"
					partial={emptyPartialFields.has("contract.largestProjectCompleted")}
					partialActive={partialActive}
				>
					<Text type="body" color="primary">
						{formatEmpty(formatToDollar(history.contract?.largestProjectCompleted))}
					</Text>
				</HistoryCardItem>
				<Separator orientation="horizontal" className="bg-surface-light" />
				<HistoryCardItem
					testId="history-failed-to-complete-a-project"
					label="Has failed to complete a project"
					partial={emptyPartialFields.has("contract.failedToCompleteAProject")}
					partialActive={partialActive}
				>
					<YesNoDisplay value={history.contract?.failedToCompleteAProject} />
				</HistoryCardItem>
				<Separator orientation="horizontal" className="bg-surface-light" />
				<HistoryCardItem
					testId="history-lost-a-payment-suit"
					label="Has lost a payment suit"
					partial={emptyPartialFields.has("contract.lostAPaymentSuit")}
					partialActive={partialActive}
				>
					<YesNoDisplay value={history.contract?.lostAPaymentSuit} />
				</HistoryCardItem>
			</>
		)}

		{showContractHistory && <Separator orientation="horizontal" className="bg-surface-light" />}
		<HistoryCardItem
			testId="history-caused-surety-loss"
			label="Has caused a loss to a surety"
			partial={emptyPartialFields.has("causedSuretyLoss")}
			partialActive={partialActive}
		>
			<YesNoDisplay value={history.causedSuretyLoss} />
		</HistoryCardItem>

		{showContractHistory && (
			<>
				<Separator orientation="horizontal" className="bg-surface-light" />
				<HistoryCardItem
					testId="history-gross-loss-gt-10-percent"
					label="Has experienced a gross loss on a project greater than 10% of that project's contract value in the last 3 years"
					partial={emptyPartialFields.has(
						"contract.constructionProjectsGrossLossGt10PercentLast36Months",
					)}
					partialActive={partialActive}
				>
					<YesNoDisplay
						value={history.contract?.constructionProjectsGrossLossGt10PercentLast36Months}
					/>
				</HistoryCardItem>
				<Separator orientation="horizontal" className="bg-surface-light" />
				<HistoryCardItem
					testId="history-current-project-expected-gross-loss"
					label="Expects a gross loss on a project they are currently working on"
					partial={emptyPartialFields.has("contract.currentConstructionProjectExpectedGrossLoss")}
					partialActive={partialActive}
				>
					<YesNoDisplay value={history.contract?.currentConstructionProjectExpectedGrossLoss} />
				</HistoryCardItem>
			</>
		)}
	</div>
);
